import React from "react";
import { domainExpertise } from "../../mockData";
import { useScrollAnimation } from "../../hooks/useScrollAnimation";
import { ShieldCheck, Cpu, Wifi, Terminal, Lock, Layers } from "lucide-react";

const domainIcons = [ShieldCheck, Cpu, Wifi];

const SecurityArchDiagram = ({ layers }) => (
  <div className="mt-5 mb-4">
    <div className="flex items-center gap-2 mb-3">
      <Layers size={13} className="text-cyan-400" />
      <span className="text-xs font-mono text-cyan-400/80 uppercase tracking-wider">Security Architecture — Defence in Depth</span>
    </div>
    <div className="space-y-1">
      {layers.map((l, i) => (
        <div key={i} className="relative group">
          <div
            className={`border rounded-lg px-4 py-2.5 flex items-center justify-between transition-all duration-200 hover:border-cyan-500/40 ${
              i === layers.length - 1
                ? "bg-amber-500/5 border-amber-500/20"
                : "bg-cyan-500/5 border-cyan-500/15"
            }`}
          >
            <div className="flex items-center gap-2">
              <Lock size={11} className={i === layers.length - 1 ? "text-amber-400" : "text-cyan-400"} />
              <span className="text-xs font-semibold text-slate-200">{l.layer}</span>
            </div>
            <span className="text-[10px] text-slate-500 font-mono hidden sm:block">{l.tech}</span>
          </div>
          {i < layers.length - 1 && (
            <div className="flex justify-center py-0.5">
              <div className="w-px h-2 bg-cyan-500/20"></div>
            </div>
          )}
        </div>
      ))}
    </div>
  </div>
);

const DomainCard = ({ domain, index }) => {
  const [ref, isVisible] = useScrollAnimation();
  const Icon = domainIcons[index] || Cpu;
  const isReversed = index % 2 !== 0;

  return (
    <div ref={ref} className={`fade-in-up ${isVisible ? "visible" : ""}`}>
      <div className={`grid lg:grid-cols-2 gap-8 items-center ${isReversed ? "" : ""}`}>
        {/* Image side */}
        <div className={`relative overflow-hidden rounded-xl ${isReversed ? "lg:order-2" : ""}`}>
          <div className="aspect-[16/10] relative">
            <img
              src={domain.image}
              alt={domain.title}
              className="w-full h-full object-cover rounded-xl"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-[#0a0f1c] via-[#0a0f1c]/30 to-transparent opacity-80 rounded-xl"></div>
            {/* Metric overlays */}
            <div className="absolute bottom-4 left-4 right-4 flex gap-3">
              {domain.metrics.map((m, i) => (
                <div
                  key={i}
                  className="bg-[#0a0f1c]/80 backdrop-blur-sm border border-slate-700/50 rounded-lg px-3 py-2 flex-1 text-center"
                >
                  <div className="text-base font-bold gradient-text font-mono">{m.value}</div>
                  <div className="text-[10px] text-slate-400 mt-0.5">{m.label}</div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Content side */}
        <div className={isReversed ? "lg:order-1" : ""}>
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 rounded-lg bg-cyan-500/10 border border-cyan-500/20 flex items-center justify-center">
              <Icon size={20} className="text-cyan-400" />
            </div>
            <div>
              <h3 className="text-xl font-bold text-slate-100">{domain.title}</h3>
              <p className="text-xs text-cyan-400/80 font-mono tracking-wide">{domain.subtitle}</p>
            </div>
          </div>

          <p className="text-slate-400 text-sm leading-relaxed mb-5">{domain.description}</p>

          {/* Terminal-style highlights */}
          <div className="bg-[#0d1117] border border-slate-800/50 rounded-lg p-4 mb-5 font-mono">
            <div className="flex items-center gap-2 mb-3 pb-2 border-b border-slate-800/30">
              <div className="flex gap-1.5">
                <div className="w-2.5 h-2.5 rounded-full bg-red-500/60"></div>
                <div className="w-2.5 h-2.5 rounded-full bg-yellow-500/60"></div>
                <div className="w-2.5 h-2.5 rounded-full bg-green-500/60"></div>
              </div>
              <div className="flex items-center gap-1.5 ml-2">
                <Terminal size={11} className="text-slate-600" />
                <span className="text-[10px] text-slate-600 uppercase tracking-wider">capabilities</span>
              </div>
            </div>
            {domain.highlights.map((h, i) => (
              <div key={i} className="flex items-center gap-2 py-1">
                <span className="text-cyan-500/60 text-xs select-none">$</span>
                <span className="text-slate-300 text-xs">{h}</span>
              </div>
            ))}
          </div>

          {/* Tech tags */}
          <div className="flex flex-wrap gap-1.5">
            {domain.tags.map((tag, i) => (
              <span key={i} className="skill-tag text-[11px] py-1 px-2.5">{tag}</span>
            ))}
          </div>

          {/* Security Architecture Diagram (if available) */}
          {domain.architectureLayers && (
            <SecurityArchDiagram layers={domain.architectureLayers} />
          )}
        </div>
      </div>
    </div>
  );
};

const DomainExpertise = () => {
  const [ref, isVisible] = useScrollAnimation();

  return (
    <section id="domains" className="py-24 relative grid-bg">
      <div className="max-w-7xl mx-auto px-6">
        <div ref={ref} className={`fade-in-up ${isVisible ? "visible" : ""} mb-16`}>
          <div className="flex items-center gap-3 mb-4">
            <div className="h-px w-12 bg-cyan-500/50"></div>
            <span className="text-cyan-400 text-sm font-mono font-medium tracking-wider uppercase">
              Domain Expertise
            </span>
          </div>
          <h2 className="text-3xl md:text-4xl font-bold text-slate-100">
            Deep Technical <span className="gradient-text">Specializations</span>
          </h2>
          <p className="text-slate-500 mt-3 max-w-2xl text-sm leading-relaxed">
            Three core domains where I've built deep expertise through decades of hands-on
            engineering and leadership in healthcare technology — from securing medical
            devices to architecting IoMT platforms at scale.
          </p>
        </div>

        <div className="space-y-20">
          {domainExpertise.map((domain, i) => (
            <DomainCard key={i} domain={domain} index={i} />
          ))}
        </div>
      </div>
      <div className="section-divider mt-24 mx-auto max-w-7xl"></div>
    </section>
  );
};

export default DomainExpertise;
